import { useForm } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { AnalyticsDateRangePopup } from 'src/pages/AnalyticsPage/AnalyticsDateRangePopup/AnalyticsDateRangePopup';
import moment from 'moment';
import { useAppSelector } from 'src/store';
import { DATE_FORMAT_MMM_DD_YYYY, ISO_DATE_FORMAT } from 'src/shared/constants';
import type {
  CustomDateRangeValues,
  DateRangeItem,
} from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCoCustomer.types';

const initialCustomDate = {
  from: moment().toDate(),
  to: moment().toDate(),
};
// A date range choice; picking 'Custom' opens the custom range popup instead.
type DateRangeOption = Pick<DateRangeItem, 'label' | 'disabled' | 'default'>;

interface AnalyticsDateProps {
  onDateChange?: (range: DateRangeItem) => void;
  selectOptions: DateRangeOption[];
}

export const AnalyticsDate = ({ onDateChange, selectOptions }: AnalyticsDateProps) => {
  const [customDateDefault, setCustomDateDefault] =
    React.useState<CustomDateRangeValues['date']>(initialCustomDate);
  const [openCustomPopup, setOpenCustomPopup] = React.useState(false);
  const { dateRangeItem } = useAppSelector((s) => s.analyticSlices);
  const defaultValue = dateRangeItem
    ? selectOptions.find((option) => option.label === dateRangeItem.label)
    : selectOptions.find((option) => option.default) || selectOptions[0];
  const [selectValue, setSelectValue] = React.useState(defaultValue);
  const [customLabel, setCustomLabel] = React.useState('');
  const { trigger } = useForm({
    defaultValues: {
      date: defaultValue,
    },
    resolver: yupResolver(
      object({
        date: object({
          label: stringScheme({ required: true }),
          disabled: booleanScheme(),
        }),
      }),
    ),
  });

  const handleDateChange = (e: DateRangeOption) => {
    setSelectValue(e);
    if (e.label === 'Custom') {
      return setOpenCustomPopup(true);
    }
    setCustomLabel('');
    setCustomDateDefault(initialCustomDate);
    e.label && onDateChange?.(e);
  };

  const submitCustomDateRange = ({ date }: CustomDateRangeValues) => {
    const start_time = moment(date.from).startOf('day').format(ISO_DATE_FORMAT);
    const end_time = moment(date.to).endOf('day').format(ISO_DATE_FORMAT);
    const start_time_label = moment(date.from).startOf('day').format(DATE_FORMAT_MMM_DD_YYYY);
    const end_time_label = moment(date.to).endOf('day').format(DATE_FORMAT_MMM_DD_YYYY);

    setCustomDateDefault(date);
    onDateChange?.({ label: 'Custom', start_time, end_time });
    setCustomLabel(`${start_time_label} - ${end_time_label}`);
  };

  return (
    <div className={'input-container md:min-w-52 text-[.88rem] min-w-[140px] analytic-date'}>
      <Input
        inputBlockClassName="[&_.custom--dropdown-container_.dropdown-selected-value]:truncate [&_.custom--dropdown-container_.dropdown-selected-value_span]:truncate"
        type={'select'}
        selectOptions={selectOptions}
        customLabel={customLabel}
        value={selectValue}
        onChange={(e) => {
          handleDateChange(e);
        }}
        onBlur={() => trigger('date')}
      />
      <AnalyticsDateRangePopup
        theme="customer"
        open={openCustomPopup}
        setOpen={setOpenCustomPopup}
        dateDefault={customDateDefault}
        submitDateFilter={submitCustomDateRange}
      />
    </div>
  );
};
